import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API = `${API_BASE}/customer-auth`;

export default function CustomerPortalForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      // Deliberately show the same success message whether or not the email
      // matched an account — this avoids letting the form be used to check
      // which email addresses have portal access.
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Only surface a genuine server/network error, not "email not found"
        if (res.status >= 500) throw new Error(data?.error || data?.message || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950">
            <Anchor size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Reset Password</h1>
          <p className="text-sm text-slate-500 mt-1">
            {submitted ? "Check your inbox for a reset link" : "Enter your email and we'll send you a reset link"}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
                If an account exists for <span className="font-semibold">{email}</span>, a password reset link has been sent. It'll expire after a short time, so use it soon.
              </div>
              <Link
                to="/portal/login"
                className="block w-full text-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-950"
                  placeholder="you@farm.co.nz"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>

              <Link
                to="/portal/login"
                className="block text-center text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                Back to Sign In
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
